// File: frontend/src/components/AttackFlow.tsx
// Attack Flow / Kill Chain visualization that replaces the old TTP Execution Flow.
// Groups MITRE ATT&CK techniques by tactic phase in a vertical timeline.

import React, { useState, useMemo } from 'react';
import useSWR from 'swr';
import { useApi } from '../hooks/useApi';
import {
    Box,
    CircularProgress,
    Alert,
    Typography,
    Paper,
    Chip,
    Collapse,
    IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { AttackFlowPhase, AttackFlowTechnique } from '../types/attackFlow';
import { cyber } from '../theme';
import { STIXBundle } from '../types/stix';

interface AttackFlowProps {
    stixUrl?: string;
}

/**
 * Ordered list of MITRE ATT&CK Enterprise tactics.
 * Maps phase_name (from STIX kill_chain_phases) to display info.
 */
const TACTIC_ORDER: { phaseName: string; tacticId: string; tacticName: string }[] = [
    { phaseName: 'reconnaissance', tacticId: 'TA0043', tacticName: 'Reconnaissance' },
    { phaseName: 'resource-development', tacticId: 'TA0042', tacticName: 'Resource Development' },
    { phaseName: 'initial-access', tacticId: 'TA0001', tacticName: 'Initial Access' },
    { phaseName: 'execution', tacticId: 'TA0002', tacticName: 'Execution' },
    { phaseName: 'persistence', tacticId: 'TA0003', tacticName: 'Persistence' },
    { phaseName: 'privilege-escalation', tacticId: 'TA0004', tacticName: 'Privilege Escalation' },
    { phaseName: 'defense-evasion', tacticId: 'TA0005', tacticName: 'Defense Evasion' },
    { phaseName: 'credential-access', tacticId: 'TA0006', tacticName: 'Credential Access' },
    { phaseName: 'discovery', tacticId: 'TA0007', tacticName: 'Discovery' },
    { phaseName: 'lateral-movement', tacticId: 'TA0008', tacticName: 'Lateral Movement' },
    { phaseName: 'collection', tacticId: 'TA0009', tacticName: 'Collection' },
    { phaseName: 'command-and-control', tacticId: 'TA0011', tacticName: 'Command and Control' },
    { phaseName: 'exfiltration', tacticId: 'TA0010', tacticName: 'Exfiltration' },
    { phaseName: 'impact', tacticId: 'TA0040', tacticName: 'Impact' },
];

/** Gradient colors for the timeline — from red (initial) to pink (C2/end) */
const TIMELINE_COLORS = [
    '#FF4444', // Reconnaissance
    '#FF5533', // Resource Development
    '#FF6622', // Initial Access
    '#FF7711', // Execution
    '#FF8800', // Persistence
    '#FF9922', // Privilege Escalation
    '#FFAA44', // Defense Evasion
    '#FFBB55', // Credential Access
    '#DDAA66', // Discovery
    '#BB9977', // Lateral Movement
    '#CC88AA', // Collection
    '#DD77BB', // Command and Control
    '#CC66CC', // Exfiltration
    '#BB55DD', // Impact
];

/**
 * Parse STIX bundle to extract Attack Flow phases.
 * Groups attack-pattern objects by their kill_chain_phases tactic.
 */
function parseStixToAttackFlow(bundle: STIXBundle): AttackFlowPhase[] {
    // Build a map from phase name to techniques
    const phaseMap = new Map<string, AttackFlowTechnique[]>();

    for (const obj of bundle.objects) {
        if (obj.type !== 'attack-pattern') continue;

        const killChainPhases = obj.kill_chain_phases as Array<{ kill_chain_name: string; phase_name: string }> | undefined;
        if (!killChainPhases || killChainPhases.length === 0) continue;

        // Extract MITRE technique ID from external_references
        let techniqueId = obj.x_mitre_id || '';
        if (!techniqueId && obj.external_references) {
            const mitreRef = (obj.external_references as Array<{ source_name: string; external_id?: string }>)
                .find(ref => ref.source_name === 'mitre-attack');
            if (mitreRef?.external_id) {
                techniqueId = mitreRef.external_id;
            }
        }

        const technique: AttackFlowTechnique = {
            id: techniqueId || obj.id,
            name: obj.name || 'Unknown Technique',
            description: obj.description,
        };

        // A technique can belong to multiple tactics
        for (const phase of killChainPhases) {
            if (phase.kill_chain_name !== 'mitre-attack') continue;
            const existing = phaseMap.get(phase.phase_name) || [];
            existing.push(technique);
            phaseMap.set(phase.phase_name, existing);
        }
    }

    // Build ordered phases, only including tactics that have techniques
    const phases: AttackFlowPhase[] = [];
    for (const tacticDef of TACTIC_ORDER) {
        const techniques = phaseMap.get(tacticDef.phaseName);
        if (techniques && techniques.length > 0) {
            phases.push({
                tacticId: tacticDef.tacticId,
                tacticName: tacticDef.tacticName,
                phaseName: tacticDef.phaseName,
                techniques,
            });
        }
    }

    return phases;
}

/** Get timeline color for a phase based on its position in the tactic order */
function getPhaseColor(phaseName: string): string {
    const idx = TACTIC_ORDER.findIndex(t => t.phaseName === phaseName);
    return idx >= 0 ? TIMELINE_COLORS[idx] : '#888';
}

const AttackFlow: React.FC<AttackFlowProps> = ({ stixUrl }) => {
    const { simpleTextFetcher } = useApi();
    const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());

    const { data: stixJsonString, error, isLoading } = useSWR(stixUrl, simpleTextFetcher);

    const phases = useMemo<AttackFlowPhase[]>(() => {
        if (!stixJsonString) return [];
        try {
            const bundle: STIXBundle = JSON.parse(stixJsonString);
            return parseStixToAttackFlow(bundle);
        } catch {
            return [];
        }
    }, [stixJsonString]);

    const togglePhase = (phaseName: string) => {
        setExpandedPhases(prev => {
            const next = new Set(prev);
            if (next.has(phaseName)) {
                next.delete(phaseName);
            } else {
                next.add(phaseName);
            }
            return next;
        });
    };

    if (!stixUrl) {
        return <Alert severity="info">No STIX bundle available for Attack Flow visualization.</Alert>;
    }
    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Alert severity="error">Could not load data for Attack Flow.</Alert>;
    }
    if (phases.length === 0) {
        return <Alert severity="info">No attack pattern data found in the STIX bundle.</Alert>;
    }

    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" gutterBottom>
                Attack Flow
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Kill chain phases observed in this report, based on MITRE ATT&CK® tactics.
            </Typography>

            <Box sx={{ position: 'relative', pl: { xs: 3, sm: 5 } }}>
                {/* Vertical timeline line with gradient */}
                <Box
                    sx={{
                        position: 'absolute',
                        left: { xs: 12, sm: 20 },
                        top: 0,
                        bottom: 0,
                        width: 3,
                        background: `linear-gradient(to bottom, ${phases.map((p, i) => {
                            const color = getPhaseColor(p.phaseName);
                            const pct = phases.length > 1 ? (i / (phases.length - 1)) * 100 : 50;
                            return `${color} ${pct}%`;
                        }).join(', ')})`,
                        borderRadius: 2,
                    }}
                />

                {phases.map((phase, index) => {
                    const isExpanded = expandedPhases.has(phase.phaseName);
                    const color = getPhaseColor(phase.phaseName);

                    return (
                        <Box
                            key={phase.phaseName}
                            sx={{
                                position: 'relative',
                                mb: index < phases.length - 1 ? 2 : 0,
                            }}
                        >
                            {/* Timeline dot */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: { xs: -24, sm: -36 },
                                    top: 16,
                                    width: 14,
                                    height: 14,
                                    borderRadius: '50%',
                                    bgcolor: color,
                                    border: '2px solid',
                                    borderColor: 'background.paper',
                                    boxShadow: `0 0 8px ${color}60`,
                                    zIndex: 1,
                                }}
                            />

                            {/* Phase card */}
                            <Paper
                                onClick={() => togglePhase(phase.phaseName)}
                                sx={{
                                    p: 2,
                                    cursor: 'pointer',
                                    borderLeft: `3px solid ${color}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: `${color}10`,
                                        borderLeftColor: color,
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {phase.tacticName}
                                        </Typography>
                                        <Chip
                                            label={phase.tacticId}
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.7rem',
                                                fontFamily: cyber.fontMono,
                                                bgcolor: `${color}30`,
                                                color: color,
                                                fontWeight: 600,
                                            }}
                                        />
                                        <Chip
                                            label={`${phase.techniques.length} technique${phase.techniques.length !== 1 ? 's' : ''}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.7rem',
                                                borderColor: 'divider',
                                            }}
                                        />
                                    </Box>
                                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>

                                <Collapse in={isExpanded} timeout={300}>
                                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {phase.techniques.map((tech, techIdx) => (
                                            <Box
                                                key={`${tech.id}-${techIdx}`}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    bgcolor: 'rgba(0,0,0,0.15)',
                                                    borderLeft: `2px solid ${color}50`,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        label={tech.id}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.7rem',
                                                            fontFamily: cyber.fontMono,
                                                            bgcolor: `${color}20`,
                                                            color: color,
                                                        }}
                                                    />
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {tech.name}
                                                    </Typography>
                                                </Box>
                                                {tech.description && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: 'block', mt: 0.5 }}
                                                    >
                                                        {tech.description.length > 200
                                                            ? `${tech.description.substring(0, 200)}...`
                                                            : tech.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </Collapse>
                            </Paper>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default AttackFlow;
